/**
 * Hide and Seek Teams — setting up a coin toss, or changing one: what it
 * decides (who hides over some rounds, or whose Team Captain pick plays
 * first), whether the winner hides first or their captain chooses, and its
 * rounds. Only rounds still in setup, not loaded and on no other toss can be
 * linked; a picks toss takes one pick from each team.
 */

import { useState } from 'react';
import {
  HNST_COIN_TOSS_KIND_LABELS,
  HNST_COIN_TOSS_MODE_LABELS,
  HNST_TEAMS,
  isHnstSettings,
  type Game,
  type HnstCoinToss,
  type HnstCoinTossKind,
  type HnstCoinTossMode,
} from '@swtorbit/shared-socket-events';
import { useHnstContext } from '../../contexts/HnstContext';

const KINDS: HnstCoinTossKind[] = ['rounds', 'picks'];
const MODES: HnstCoinTossMode[] = ['winner_hides', 'winner_chooses'];

const KIND_HINTS: Record<HnstCoinTossKind, string> = {
  rounds: 'The toss decides who hides in the first of its rounds; the teams then take turns over them.',
  picks: "The toss decides whose Team Captain pick is played first: that pick moves ahead of the other. Each team hides in its own pick.",
};

export function HnstCoinTossForm({ game, toss, onDone }: { game: Game; toss: HnstCoinToss | null; onDone: () => void }) {
  const actions = useHnstContext();
  const [kind, setKind] = useState<HnstCoinTossKind>(toss?.kind ?? 'rounds');
  const [mode, setMode] = useState<HnstCoinTossMode>(toss?.mode ?? 'winner_hides');
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(game.rounds.flatMap((r, i) => (toss && isHnstSettings(r.settings) && r.settings.coinTossId === toss.id ? [i] : []))),
  );
  const [error, setError] = useState<string | null>(null);

  // The rounds this toss may take.
  const choices = game.rounds.flatMap((round, index) => {
    const settings = round.settings;
    if (!isHnstSettings(settings) || round.status !== 'setup' || game.loadedRoundIndex === index) return [];
    if (settings.coinTossId != null && settings.coinTossId !== toss?.id) return [];
    if ((kind === 'picks') !== Boolean(settings.ownerTeam)) return [];
    const where = round.hnsLocation?.areaName ?? 'location detected at start';
    const label = settings.ownerTeam ? `${HNST_TEAMS[settings.ownerTeam].name}' pick — ${where}` : where;
    return [{ index, label }];
  });

  const toggle = (index: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  const submit = async () => {
    const roundIndexes = [...selected].filter((i) => choices.some((c) => c.index === i)).sort((a, b) => a - b);
    const res = toss
      ? await actions.updateCoinToss(toss.id, mode, roundIndexes)
      : await actions.createCoinToss(kind, mode, roundIndexes);
    if (res.success) onDone();
    else setError(res.error ?? 'Something went wrong');
  };

  return (
    <div className="d-flex flex-column gap-sm">
      {error && <div className="alert alert--red">{error}</div>}
      <div className="field">
        <label htmlFor="hnst-toss-kind">What it decides</label>
        <select
          className="select"
          id="hnst-toss-kind"
          value={kind}
          disabled={toss !== null}
          onChange={(e) => {
            setKind(e.target.value as HnstCoinTossKind);
            setSelected(new Set());
          }}
        >
          {KINDS.map((k) => <option key={k} value={k}>{HNST_COIN_TOSS_KIND_LABELS[k]}</option>)}
        </select>
        <p className="field__hint fs-xs">{KIND_HINTS[kind]}</p>
      </div>

      <div className="field">
        <label htmlFor="hnst-toss-mode">The winner</label>
        <select className="select" id="hnst-toss-mode" value={mode} onChange={(e) => setMode(e.target.value as HnstCoinTossMode)}>
          {MODES.map((m) => <option key={m} value={m}>{HNST_COIN_TOSS_MODE_LABELS[m]}</option>)}
        </select>
        {mode === 'winner_chooses' && (
          <p className="field__hint fs-xs">
            {kind === 'picks' ? 'Their Team Captain chooses whose pick goes first' : 'Their Team Captain chooses to hide or seek first'} in
            their client. If they don&apos;t within 30 seconds, it is chosen at random. You can choose for them.
          </p>
        )}
      </div>

      <div className="field">
        <label>Rounds</label>
        {choices.length === 0 ? (
          <p className="field__hint">
            {kind === 'picks'
              ? 'No Team Captain picks to link yet: make a round a pick in its settings.'
              : 'No rounds to link: only rounds in setup, not loaded and on no other toss.'}
          </p>
        ) : (
          choices.map(({ index, label }) => (
            <div key={index} className="form-check">
              <input
                type="checkbox"
                id={`hnst-toss-round-${index}`}
                className="form-check-input"
                checked={selected.has(index)}
                onChange={() => toggle(index)}
              />
              <label htmlFor={`hnst-toss-round-${index}`} className="form-check-label">Round {index + 1} — {label}</label>
            </div>
          ))
        )}
        {kind === 'picks' && <p className="field__hint fs-xs">Choose one pick from each team.</p>}
      </div>

      {toss?.flip && <p className="field__hint fs-xs">Saving changes undoes the flip: flip it again after.</p>}
      <div className="d-flex gap-xs justify-content-end">
        <button className="btn btn--outline btn--slate" onClick={onDone}>Cancel</button>
        <button className="btn btn--green" onClick={submit} disabled={selected.size === 0}>
          {toss ? 'Save Coin Toss' : 'Add Coin Toss'}
        </button>
      </div>
    </div>
  );
}
